/**
 * Pure camera and explode maths (no three.js, no DOM) shared by the scene, the
 * asset catalogue script and the unit tests. Vectors are [x, y, z] in asset
 * metres, angles in degrees: azimuth measured from +Z towards +X around Y,
 * elevation above the horizon.
 */
import type { CameraPose } from '../types';

export type Vec3 = [number, number, number];

export const DEFAULT_FOV = 45;
/** Camera flights take between these bounds (PLAN 4.3: 800 to 1500 ms). */
export const FLIGHT_MIN_MS = 800;
export const FLIGHT_MAX_MS = 1500;

export interface OrbitPov {
  azimuthDeg: number;
  elevationDeg: number;
  /** Unity zoom, a distance in Unity units; only the direction is reused. */
  zoom: number;
}

/** Parses the Unity "rotX_rotY_zoom" string; null when malformed. */
export function parsePov(pov: string | undefined | null): OrbitPov | null {
  if (!pov) return null;
  const parts = pov.split('_').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [azimuthDeg, elevationDeg, zoom] = parts;
  return { azimuthDeg, elevationDeg: Math.max(-89, Math.min(89, elevationDeg)), zoom };
}

/** Direction unit vector for an azimuth and elevation. */
export function orbitDirection(azimuthDeg: number, elevationDeg: number): Vec3 {
  const az = (azimuthDeg * Math.PI) / 180;
  const el = (elevationDeg * Math.PI) / 180;
  return [Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az)];
}

/** Azimuth (0 to 360) and elevation of the vector from `target` to `position`. */
export function directionAngles(position: Vec3, target: Vec3): { azimuthDeg: number; elevationDeg: number; distance: number } {
  const dx = position[0] - target[0];
  const dy = position[1] - target[1];
  const dz = position[2] - target[2];
  const distance = Math.hypot(dx, dy, dz);
  if (distance === 0) return { azimuthDeg: 0, elevationDeg: 0, distance: 0 };
  const elevationDeg = (Math.asin(Math.max(-1, Math.min(1, dy / distance))) * 180) / Math.PI;
  const azimuthDeg = ((((Math.atan2(dx, dz) * 180) / Math.PI) % 360) + 360) % 360;
  return { azimuthDeg, elevationDeg, distance };
}

/** Distance at which a sphere of `radius` fills `fill` of the narrower field of view. */
export function fitDistance(radius: number, fovDeg = DEFAULT_FOV, aspect = 16 / 9, fill = 0.8): number {
  const vFov = (fovDeg * Math.PI) / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const fov = Math.min(vFov, hFov);
  const r = Math.max(radius, 1e-3);
  return r / fill / Math.sin(fov / 2);
}

export function orbitPose(target: Vec3, azimuthDeg: number, elevationDeg: number, distance: number, fov = DEFAULT_FOV): CameraPose {
  const d = orbitDirection(azimuthDeg, elevationDeg);
  return {
    position: [target[0] + d[0] * distance, target[1] + d[1] * distance, target[2] + d[2] * distance],
    target: [...target],
    fov,
  };
}

/** Main pose for an asset: the Unity POV's direction, a distance that frames the bounding sphere. */
export function poseFromPov(pov: string | undefined | null, centre: Vec3, radius: number, fovDeg = DEFAULT_FOV, fallback: OrbitPov = { azimuthDeg: 212, elevationDeg: 18, zoom: 0 }): CameraPose {
  const p = parsePov(pov) ?? fallback;
  const elevation = Math.max(5, p.elevationDeg);
  return orbitPose(centre, p.azimuthDeg, elevation, fitDistance(radius, fovDeg), fovDeg);
}

/**
 * Pose that frames one subsystem: keeps the current viewing direction (so the
 * flight does not swing around), moves to a fitted distance and keeps the camera
 * at least `minElevationDeg` above the horizon.
 */
export function fitSubsystemPose(
  nodeCentre: Vec3,
  nodeRadius: number,
  cameraPosition: Vec3,
  fovDeg = DEFAULT_FOV,
  aspect = 16 / 9,
  minElevationDeg = 8,
  minDistance = 0.3,
): CameraPose {
  const { azimuthDeg, elevationDeg } = directionAngles(cameraPosition, nodeCentre);
  const el = Math.max(minElevationDeg, elevationDeg);
  const distance = Math.max(minDistance, fitDistance(nodeRadius, fovDeg, aspect, 0.55));
  return orbitPose(nodeCentre, azimuthDeg, el, distance, fovDeg);
}

/** Free space kept around a framed box, as fractions of the half view (0.2 leaves the outer 10 % of the view empty). */
export interface FitMargins {
  /** Left and right. */
  side: number;
  /** Above the box: the HUD rows sit over the top of the stage. */
  top: number;
  /** Below the box: the event log sits over the bottom left. */
  bottom: number;
}

/** Margins of the main framing: room for the two HUD rows at the top and the event log at the bottom. */
export const MAIN_MARGINS: FitMargins = { side: 0.1, top: 0.44, bottom: 0.14 };
/** Elevation range of the main framing for ground assets: a slightly elevated three-quarter view. */
export const MAIN_ELEVATION_DEG = { min: 20, max: 35 } as const;

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/**
 * Pose that frames an axis-aligned box seen from (azimuth, elevation): every
 * corner of the box lands inside the view with the given margins. The target
 * stays on the box's vertical axis (so orbiting turns about the asset) and
 * moves up or down to centre the box between the top and bottom margins; the
 * distance is solved for the tighter of the horizontal and vertical extents.
 * The projection is perspective, so both are refined iteratively (they
 * converge within a few steps; 40 are run).
 */
export function fitBoxPose(
  min: Vec3,
  max: Vec3,
  azimuthDeg: number,
  elevationDeg: number,
  fovDeg = DEFAULT_FOV,
  aspect = 16 / 9,
  margins: FitMargins = MAIN_MARGINS,
  geometryPoints?: Vec3[],
): CameraPose {
  const toCamera = orbitDirection(azimuthDeg, elevationDeg);
  const forward: Vec3 = [-toCamera[0], -toCamera[1], -toCamera[2]];
  // Right is horizontal (forward x world up); straight down or up falls back to +X.
  let right: Vec3 = [-forward[2], 0, forward[0]];
  const rl = Math.hypot(right[0], right[2]);
  right = rl < 1e-6 ? [1, 0, 0] : [right[0] / rl, 0, right[2] / rl];
  const up: Vec3 = [
    right[1] * forward[2] - right[2] * forward[1],
    right[2] * forward[0] - right[0] * forward[2],
    right[0] * forward[1] - right[1] * forward[0],
  ];
  const tanV = Math.tan((fovDeg * Math.PI) / 360);
  const tanH = tanV * aspect;
  const corners: Vec3[] = geometryPoints?.length ? geometryPoints : [];
  if (!corners.length) for (const x of [min[0], max[0]]) for (const y of [min[1], max[1]]) for (const z of [min[2], max[2]]) corners.push([x, y, z]);
  const size = Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
  const target: Vec3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
  const halfX = 1 - margins.side;
  const topY = 1 - margins.top;
  const bottomY = -(1 - margins.bottom);
  // Vertical shift of the projection per metre of target height (the camera's up axis tilts with the elevation).
  const upShare = Math.max(0.2, up[1]);
  let distance = fitDistance(Math.max(size / 2, 1e-3), fovDeg, aspect, 0.8);
  for (let i = 0; i < 40; i++) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const c of corners) {
      const q: Vec3 = [c[0] - target[0], c[1] - target[1], c[2] - target[2]];
      const depth = Math.max(dot(q, forward) + distance, distance * 0.05);
      const x = dot(q, right) / (depth * tanH);
      const y = dot(q, up) / (depth * tanV);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
    const kx = Math.max(-minX, maxX) / halfX;
    const ky = (maxY - minY) / (topY - bottomY);
    const k = Math.max(kx, ky);
    const offset = (minY + maxY) / 2 - (topY + bottomY) / 2;
    target[1] += (offset * distance * tanV) / upShare;
    distance *= k;
    if (Math.abs(k - 1) < 1e-4 && Math.abs(offset) < 1e-4) break;
  }
  return orbitPose(target, azimuthDeg, elevationDeg, distance, fovDeg);
}

/**
 * The main framing of an asset: the authored direction (the Unity POV) with
 * the elevation held to a slightly elevated three-quarter view for ground
 * assets (space assets keep theirs within +-60 degrees), at the distance and
 * target height that frame the asset's bounds with MAIN_MARGINS.
 */
export function mainPose(
  bounds: { min: Vec3; max: Vec3 },
  authoredAzimuthDeg: number,
  authoredElevationDeg: number,
  isSpace: boolean,
  fovDeg = DEFAULT_FOV,
  aspect = 16 / 9,
  margins: FitMargins = MAIN_MARGINS,
  geometryPoints?: Vec3[],
): CameraPose {
  const el = isSpace
    ? Math.max(-60, Math.min(60, authoredElevationDeg))
    : Math.max(MAIN_ELEVATION_DEG.min, Math.min(MAIN_ELEVATION_DEG.max, authoredElevationDeg));
  return fitBoxPose(bounds.min, bounds.max, authoredAzimuthDeg, el, fovDeg, aspect, margins, geometryPoints);
}

/** Azimuth of the exploded view's entry framing off a coaxial stack's axis line (the dish's boresight), degrees. */
export const EXPLODED_VIEW_OFFSET_DEG = 60;
/** An axis whose horizontal part is shorter than this points nearly straight up and has no usable azimuth. */
const MIN_HORIZONTAL_AXIS = 0.25;

/**
 * Entry azimuth for the exploded view: offsetDeg of azimuth off `axis` (a
 * coaxial stack's axis, the dish's boresight, the way it faces), so the
 * layers spread along it read side by side. Of the two sides, the one
 * towards `toward` (from the stack to the model's centre) wins, so the rest
 * of the model stands in front of or beside the stack instead of hidden
 * behind it; without a usable `toward` (absent, or pointing nearly straight
 * up or down), the side nearest the current azimuth. Null when the axis is
 * nearly vertical. Azimuths follow directionAngles (degrees from +Z towards
 * +X, 0 to 360).
 */
export function explodedViewAzimuth(currentAzimuthDeg: number, axis: Vec3, toward: Vec3 | null = null, offsetDeg = EXPLODED_VIEW_OFFSET_DEG): number | null {
  if (Math.hypot(axis[0], axis[2]) < MIN_HORIZONTAL_AXIS) return null;
  const norm = (a: number) => ((a % 360) + 360) % 360;
  const azimuthOf = (v: Vec3) => (Math.atan2(v[0], v[2]) * 180) / Math.PI;
  const usableToward = toward !== null && Math.hypot(toward[0], toward[2]) >= MIN_HORIZONTAL_AXIS * Math.hypot(toward[0], toward[1], toward[2]);
  const reference = toward !== null && usableToward ? azimuthOf(toward) : currentAzimuthDeg;
  const gap = (a: number) => {
    const d = Math.abs(norm(a) - norm(reference));
    return Math.min(d, 360 - d);
  };
  const axisAzimuth = azimuthOf(axis);
  const left = axisAzimuth + offsetDeg;
  const right = axisAzimuth - offsetDeg;
  return norm(gap(left) <= gap(right) ? left : right);
}

/** Smoothstep-style ease in and out for the flights. */
export function easeInOutCubic(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/** Flight duration: 0 stays 0 (jump), anything else is clamped to the design range. */
export function flightDuration(requestedMs: number | undefined, reducedMotion: boolean): number {
  if (reducedMotion) return 0;
  if (!requestedMs || requestedMs <= 0) return 0;
  return Math.max(FLIGHT_MIN_MS, Math.min(FLIGHT_MAX_MS, requestedMs));
}

/** Grid cell size that reads at the asset's scale (1, 2, 5, 10, ... metres). */
export function gridCellFor(assetRadius: number): number {
  const raw = Math.max(0.25, assetRadius / 8);
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / pow;
  const step = m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10;
  return step * pow;
}

export function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
